(() => {
  const SECTION_SELECTOR = [
    '.blaze-hero',
    '.blaze-benefits',
    '.blaze-uses',
    '.blaze-flavor',
    '.blaze-ingredients',
    '.blaze-video',
    '.blaze-comparison',
    '.blaze-reviews',
    '.blaze-faq',
    '.blaze-cta',
  ].join(',');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canObserve = 'IntersectionObserver' in window;
  let observer;

  const cleanElement = (element) => {
    element.classList.remove('is-blaze-visible');
    element.removeAttribute('data-blaze-reveal');
    element.style.removeProperty('--blaze-motion-delay');
  };

  const revealElement = (element) => {
    if (element.classList.contains('is-blaze-visible')) return;

    element.classList.add('is-blaze-visible');
    observer?.unobserve(element);

    let fallbackTimeout;
    const handleAnimationEnd = (event) => {
      if (event.target !== element) return;

      window.clearTimeout(fallbackTimeout);
      element.removeEventListener('animationend', handleAnimationEnd);
      cleanElement(element);
    };

    element.addEventListener('animationend', handleAnimationEnd);

    fallbackTimeout = window.setTimeout(() => {
      element.removeEventListener('animationend', handleAnimationEnd);
      if (element.hasAttribute('data-blaze-reveal')) cleanElement(element);
    }, 1500);
  };

  const getObserver = () => {
    if (observer) return observer;

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) revealElement(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.12,
      }
    );

    return observer;
  };

  const markGroup = (
    section,
    selector,
    effect = 'rise',
    startDelay = 0,
    delayStep = 70
  ) => {
    section.querySelectorAll(selector).forEach((element, index) => {
      if (element.hasAttribute('data-blaze-reveal')) return;

      element.setAttribute('data-blaze-reveal', effect);
      element.style.setProperty(
        '--blaze-motion-delay',
        `${Math.min(startDelay + index * delayStep, 320)}ms`
      );
    });
  };

  const configureSection = (section) => {
    if (section.dataset.blazeMotionConfigured === 'true') return;

    section.dataset.blazeMotionConfigured = 'true';

    if (section.matches('.blaze-hero')) {
      markGroup(
        section,
        [
          '.blaze-hero__eyebrow',
          '.blaze-hero__title',
          '.blaze-hero__description',
          '.blaze-hero__offer',
          '.blaze-hero__buy-area',
          '.blaze-hero__note',
        ].join(','),
        'slide-start',
        60,
        75
      );
      markGroup(section, '.blaze-hero__media', 'zoom', 140);
      markGroup(section, '.blaze-hero__trust-item', 'rise', 260, 55);
      return;
    }

    if (section.matches('.blaze-benefits')) {
      markGroup(section, '.blaze-benefits__media', 'slide-start', 40);
      markGroup(
        section,
        [
          '.blaze-benefits__eyebrow',
          '.blaze-benefits__heading',
          '.blaze-benefits__description',
          '.blaze-benefits__uses',
        ].join(','),
        'slide-end',
        70,
        70
      );
      markGroup(section, '.blaze-benefits__item', 'rise', 150, 55);
      markGroup(section, '.blaze-benefits__button-wrap', 'rise', 260);
      return;
    }

    markGroup(
      section,
      [
        '.blaze-section__header > .blaze-section__eyebrow',
        '.blaze-section__header > .blaze-section__heading',
        '.blaze-section__header > .blaze-section__intro',
      ].join(','),
      'rise',
      0,
      65
    );

    if (section.matches('.blaze-uses')) {
      markGroup(section, '.blaze-use-card', 'rise', 90, 65);
    } else if (section.matches('.blaze-flavor')) {
      markGroup(section, '.blaze-flavor__stage', 'rise', 90, 80);
    } else if (section.matches('.blaze-ingredients')) {
      markGroup(section, '.blaze-ingredients__notice', 'zoom', 80);
      markGroup(section, '.blaze-ingredient', 'rise', 120, 60);
    } else if (section.matches('.blaze-video')) {
      markGroup(section, '.blaze-video__media', 'slide-start', 30);
      markGroup(
        section,
        [
          '.blaze-video__content > .blaze-section__eyebrow',
          '.blaze-video__content > .blaze-section__heading',
          '.blaze-video__content > .blaze-section__intro',
        ].join(','),
        'slide-end',
        80,
        75
      );
    } else if (section.matches('.blaze-comparison')) {
      markGroup(section, '.blaze-comparison__table-wrap', 'zoom', 100);
    } else if (section.matches('.blaze-reviews')) {
      markGroup(section, '.blaze-ingredients__notice', 'zoom', 180);
    } else if (section.matches('.blaze-faq')) {
      markGroup(section, '.blaze-faq__item', 'rise', 70, 55);
    } else if (section.matches('.blaze-cta')) {
      markGroup(section, '.blaze-cta__media', 'slide-start', 30);
      markGroup(
        section,
        [
          '.blaze-cta__content > .blaze-section__eyebrow',
          '.blaze-cta__content > .blaze-section__heading',
          '.blaze-cta__content > .blaze-section__intro',
          '.blaze-cta__offer',
          '.blaze-cta__price',
          '.blaze-cta__content > product-form-component',
          '.blaze-cta__content > .blaze-button',
          '.blaze-cta__note',
          '.blaze-cta__badges',
        ].join(','),
        'slide-end',
        70,
        55
      );
    }
  };

  const findSections = (scope) => {
    const sections = [];

    if (scope instanceof Element && scope.matches(SECTION_SELECTOR)) {
      sections.push(scope);
    }

    scope.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      sections.push(section);
    });

    return sections;
  };

  const initMotion = (scope = document) => {
    if (reduceMotion.matches || !canObserve) return;

    const sections = findSections(scope);
    if (sections.length === 0) return;

    sections.forEach(configureSection);
    document.documentElement.classList.add('blaze-motion-ready');

    sections.forEach((section) => {
      section.querySelectorAll('[data-blaze-reveal]').forEach((element) => {
        getObserver().observe(element);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initMotion(), {
      once: true,
    });
  } else {
    initMotion();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initMotion(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    event.target.querySelectorAll('[data-blaze-reveal]').forEach(revealElement);
  });
})();
